 
import React, { useState, useCallback, useRef } from 'react';
import { Row, Col, Card, Input, Typography, Button, message, Drawer, Tooltip, Select, Tabs, Tag, Modal, Space } from 'antd';
import { Controller } from 'react-hook-form';
import {
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
  DragOutlined,
  DeleteOutlined,
  SettingOutlined,
  CheckOutlined,
  CameraOutlined,
  FontSizeOutlined,
  NumberOutlined,
  CheckSquareOutlined,
  DotChartOutlined,
  CaretDownOutlined,
  FileTextOutlined,
  HolderOutlined,
  TagsOutlined,
  AppstoreOutlined,
  CopyOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import DynamicFormComponent from './DynamicFormComponent ';
import FloatingToolbox from './FloatingFormToolbox';
import InspectionBtn from '@/components/InspectionBtn';
import toast from "react-hot-toast";

const { TabPane } = Tabs;
const { Paragraph } = Typography;

// Move EditableLabel outside the main component to prevent recreation on every render
const EditableLabel = ({ 
  name, 
  componentIndex, 
  isCommon = false, 
  component, 
  unitIndex = null,
  editingLabel,
  labelValue,
  setLabelValue,
  handleLabelEdit,
  handleLabelSave,
  handleLabelCancel,
  handleEditOptions,
  handleRemoveComponent,
  componentHasOptions,
  isEditMode,
  editingGuidance,
  guidanceValue,
  setGuidanceValue,
  handleGuidanceEdit,
  handleGuidanceSave,
  handleGuidanceCancel
}) => {
  const isEditing = editingLabel?.index === componentIndex && editingLabel?.isCommon === isCommon;
  const hasOptions = componentHasOptions(component);
  const isEditingGuidanceState = editingGuidance?.componentIndex === componentIndex && editingGuidance?.isCommon === isCommon;

  if (!isEditMode && unitIndex !== 0) {
    return (
      <div className='flex justify-between'>
        <Typography.Text strong style={{ display: "block", marginBottom: "8px" }}>
          {name}
          {component?.unit && ` (${component.unit})`}
          {component?.required && <span style={{ color: "red" }}>*</span>}
        </Typography.Text>

        {component?.guidance_text && (
          <Tooltip title={component.guidance_text}>
            <QuestionCircleOutlined 
              className="me-3" 
              style={{ color: "gray", fontSize: "14px", cursor: "pointer" }} 
            />
          </Tooltip>
        )}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="mb-2">
        <Input
          size="small"
          value={labelValue}
          onChange={(e) => setLabelValue(e.target.value)}
          onPressEnter={handleLabelSave}
          className="mb-1"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              handleLabelCancel();
            }
          }}
        />
        <div className="flex gap-1">
          <Button size="small" type="primary" onClick={handleLabelSave}>
            <CheckOutlined />
          </Button>
          <Button size="small" onClick={handleLabelCancel}>
            <CloseOutlined />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Typography.Text strong>
          {name}
          {component?.unit && ` (${component.unit})`}
          {component?.required && <span style={{ color: "red" }}>*</span>}
        </Typography.Text>
        <div className="flex gap-1">
          <Button
            size="small"
            type="text"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleLabelEdit(componentIndex, name, isCommon);
            }}
            className="opacity-60 hover:opacity-100"
          />
          {hasOptions && (
            <Tooltip title="Edit Options">
              <Button
                size="small"
                type="text"
                icon={<SettingOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditOptions(componentIndex, isCommon);
                }}
                className="opacity-60 hover:opacity-100 text-green-600"
              />
            </Tooltip>
          )}
          <Tooltip title="Remove Component">
            <Button
              size="small"
              type="text"
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveComponent(componentIndex, isCommon);
              }}
              className="opacity-60 hover:opacity-100 text-red-600"
            />
          </Tooltip>
        </div>
      </div>

      {/* Guidance Text Section - Show only for first unit (unitIndex 0) or common components */}
      {(unitIndex === 0 || isCommon || unitIndex === null) && (
        <div className="mb-2">
          {isEditingGuidanceState ? (
            <div>
              <Input.TextArea
                key={`guidance-${componentIndex}-${isCommon ? 'common' : 'unit'}`}
                value={guidanceValue}
                onChange={(e) => setGuidanceValue(e.target.value)}
                placeholder="Enter guidance text..."
                rows={2}
                size="small"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    handleGuidanceSave();
                  }
                  if (e.key === 'Escape') {
                    handleGuidanceCancel();
                  }
                }}
              />
              <div className="flex gap-1 mt-1">
                <Button 
                  size="small" 
                  type="primary" 
                  onClick={handleGuidanceSave}
                >
                  <CheckOutlined />
                </Button>
                <Button 
                  size="small" 
                  onClick={handleGuidanceCancel}
                >
                  <CloseOutlined />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <Typography.Text 
                type="secondary" 
                className="text-xs"
                style={{ fontStyle: 'italic' }}
              >
                {component.guidance_text ? component.guidance_text : 'No guidance set'}
              </Typography.Text>
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGuidanceEdit(componentIndex, component.guidance_text, isCommon);
                }}
                className="opacity-60 hover:opacity-100 text-blue-600"
                title="Edit Guidance"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const InspectionStepContent = ({
  step,
  stepIndex,
  control,
  onFormChange,
  onUnitDataChange,
  onCommonDataChange,
  onImageChange,
  onRemoveImage,
  onDragOver,
  onDragLeave,
  onDrop,
  dragOverTarget,
  onStepUpdate,
  isNewStep = false
}) => {
   // Changed: Default edit mode to true
  const [isEditMode, setIsEditMode] = useState(true);
  const [toolboxVisible, setToolboxVisible] = useState(true); // Also default toolbox to visible
  const [draggedComponent, setDraggedComponent] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelValue, setLabelValue] = useState('');
  const [dragOverZone, setDragOverZone] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [editingOptions, setEditingOptions] = useState(null); // { componentIndex, isCommon }
  const [optionsModalVisible, setOptionsModalVisible] = useState(false);
  const [tempOptions, setTempOptions] = useState([]);
  
  // NEW: States for editing description and guidance
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingGuidance, setEditingGuidance] = useState(null); // { componentIndex, isCommon }
  const [guidanceValue, setGuidanceValue] = useState(''); // Local state for guidance text editing
  const textAreaRef = useRef(null); // Ref for textarea to manage cursor position

  // Available form components for the toolbox
  const formComponents = [
    {
      type: 'image',
      icon: CameraOutlined,
      label: 'Image',
      color: '#1890ff',
      defaultProps: {
        name: 'new_image',
        label: 'Image Upload',
        type: 'image',
        required: false,
        isCommon: false,
        placeholderimage: ''
      }
    },
    {
      type: 'textarea',
      icon: FileTextOutlined,
      label: 'Text Area',
      color: '#52c41a',
      defaultProps: {
        name: 'new_textarea',
        label: 'Description',
        type: 'textarea',
        required: false,
        isCommon: false,
        placeholder: 'Enter text...'
      }
    },
    {
      type: 'text',
      icon: FontSizeOutlined,
      label: 'Text Box',
      color: '#722ed1',
      defaultProps: {
        name: 'new_text',
        label: 'Text Input',
        type: 'text',
        required: false,
        isCommon: false,
        placeholder: 'Enter text...'
      }
    },
    {
      type: 'integer',
      icon: NumberOutlined,
      label: 'Number',
      color: '#fa8c16',
      defaultProps: {
        name: 'new_number',
        label: 'Number Input',
        type: 'integer',
        required: false,
        isCommon: false,
        placeholder: '0'
      }
    },
    {
      type: 'checkbox',
      icon: CheckSquareOutlined,
      label: 'Checkbox',
      color: '#13c2c2',
      defaultProps: {
        name: 'new_checkbox',
        label: 'Checkbox Options',
        type: 'checkbox',
        required: false,
        isCommon: false,
        options: ['Option 1', 'Option 2']
      }
    },
    {
      type: 'radio',
      icon: DotChartOutlined,
      label: 'Radio',
      color: '#eb2f96',
      defaultProps: {
        name: 'new_radio',
        label: 'Radio Options',
        type: 'radio',
        required: false,
        isCommon: false,
        options: ['Option 1', 'Option 2']
      }
    },
    {
      type: 'dropdown',
      icon: CaretDownOutlined,
      label: 'Dropdown',
      color: '#f5222d',
      defaultProps: {
        name: 'new_dropdown',
        label: 'Dropdown Selection',
        type: 'dropdown',
        required: false,
        isCommon: false,
        options: ['Option 1', 'Option 2']
      }
    }
  ];

  const handleUnitDataChange = (stepIndex, unitIndex, componentName, value) => {
    onUnitDataChange(stepIndex, unitIndex, componentName, value);
  };

  const handleCommonDataChange = (stepIndex, componentName, value) => {
    onCommonDataChange(stepIndex, componentName, value);
  };

  const handleImageUpload = async (file, stepIndex, unitIndex, componentName, isCommon, isFromCamera) => {
    await onImageChange(file, stepIndex, unitIndex, componentName, isCommon, isFromCamera);
  };

  const handleImageRemove = (stepIndex, unitIndex, componentName, isCommon) => {
    onRemoveImage(stepIndex, unitIndex, componentName, isCommon);
  };

  // Toggle edit mode - keeping the function in case it's needed elsewhere
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    if (!isEditMode) {
      setToolboxVisible(true);
    } else {
      setToolboxVisible(false);
    }
  };

 
  // Handle drag start from toolbox
  const handleToolboxDragStart = (e, component) => {
    setDraggedComponent(component);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', JSON.stringify(component));
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDragOverZone(null);
    setDraggedComponent(null);
  };

  // Handle drop on any unit (modified to work for all units)
  const handleUnitComponentDrop = useCallback((e, targetUnitIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverZone(null);

    try {
      const componentData = e.dataTransfer.getData('text/plain');

      if (!componentData) {
        return;
      }

      const component = JSON.parse(componentData);

      const newComponent = {
        ...component.defaultProps,
        name: `${component.defaultProps.name}_${Date.now()}`,
        id: `component_${Date.now()}`
      };

      // Add component to step configuration (only if not already exists)
      const updatedStep = { ...step };

      // Check if component already exists in step components
      const existingComponentIndex = (step.components || []).findIndex(c => c.name === newComponent.name);

      if (existingComponentIndex === -1) {
        updatedStep.components = [...(step.components || []), newComponent];
      }

      // Initialize the new component in all units
      const updatedUnits = (step.units || []).map(unit => {
        const updatedUnit = { ...unit };

        if (newComponent.type === 'image') {
          updatedUnit.images = { ...(updatedUnit.images || {}), [newComponent.name]: null };
        } else {
          const defaultValue = newComponent.type === 'checkbox' ? [] :
            newComponent.type === 'integer' ? 0 : '';
          updatedUnit.data = { ...(updatedUnit.data || {}), [newComponent.name]: defaultValue };
        }

        return updatedUnit;
      });

      updatedStep.units = updatedUnits;

      // Update parent
      if (onStepUpdate) {
        onStepUpdate(stepIndex, updatedStep);
      }

      toast.success(`Added ${component.name} component`);
    } catch (error) {
      console.error('Error adding component:', error);
      toast.error('Failed to add component');
    }

    setDraggedComponent(null);
  }, [step, stepIndex, onStepUpdate]);

  // Handle drop on common components
  const handleCommonComponentDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverZone(null);

    try {
      const componentData = e.dataTransfer.getData('text/plain');

      if (!componentData) {
        return;
      }

      const component = JSON.parse(componentData);
      const newComponent = {
        ...component.defaultProps,
        name: `common_${component.defaultProps.name}}`,
        isCommon: true,
        id: `common_component_${Date.now()}`
      };


      // Add component to step configuration
      const updatedStep = {
        ...step,
        commonComponents: [...(step.commonComponents || []), newComponent]
      };

      // Initialize the new component in common data
      const defaultValue = newComponent.type === 'checkbox' ? [] :
        newComponent.type === 'integer' ? 0 :
          newComponent.type === 'image' ? null : '';

      updatedStep.commonData = {
        ...(step.commonData || {}),
        [newComponent.name]: defaultValue
      };

      // Update parent
      if (onStepUpdate) {
        onStepUpdate(stepIndex, updatedStep);
      }

      toast.success(`Added ${component.name} to common fields`);
    } catch (error) {
      console.error('Error adding common component:', error);
      toast.error('Failed to add common component');
    }

    setDraggedComponent(null);
  }, [step, stepIndex, onStepUpdate]);

  // Handle drag over for drop zones - Modified to accept unit index
  const handleUnitDragOver = (e, unitIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverZone(`unit-${unitIndex}`);
  };

  const handleCommonDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverZone('common');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only clear if we're leaving the actual drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverZone(null);
    }
  };

  // Add component programmatically
  const handleAddComponent = (componentType, isCommon = false) => {
    const componentTemplate = formComponents.find(c => c.type === componentType);
    if (!componentTemplate) return;

    const newComponent = {
      ...componentTemplate.defaultProps,
      name: `${isCommon ? 'common_' : ''}${componentTemplate.defaultProps.name}_${Date.now()}`,
      id: `${isCommon ? 'common_' : ''}component_${Date.now()}`,
      isCommon
    };

    const updatedStep = { ...step };

    if (isCommon) {
      updatedStep.commonComponents = [...(step.commonComponents || []), newComponent];
      const defaultValue = newComponent.type === 'checkbox' ? [] :
        newComponent.type === 'integer' ? 0 :
          newComponent.type === 'image' ? null : '';
      updatedStep.commonData = {
        ...(step.commonData || {}),
        [newComponent.name]: defaultValue
      };
    } else {
      updatedStep.components = [...(step.components || []), newComponent];

      // Initialize in all units
      const updatedUnits = (step.units || []).map(unit => {
        const updatedUnit = { ...unit };

        if (newComponent.type === 'image') {
          updatedUnit.images = { ...(updatedUnit.images || {}), [newComponent.name]: null };
        } else {
          const defaultValue = newComponent.type === 'checkbox' ? [] :
            newComponent.type === 'integer' ? 0 : '';
          updatedUnit.data = { ...(updatedUnit.data || {}), [newComponent.name]: defaultValue };
        }

        return updatedUnit;
      });

      updatedStep.units = updatedUnits;
    }

    if (onStepUpdate) {
      onStepUpdate(stepIndex, updatedStep);
    }

    toast.success(`Added ${componentTemplate.name} ${isCommon ? 'to common fields' : 'component'}`);
  };

  // Modified: Updated image component creation to remove label duplication
  const handleAddImageComponent = (isCommon = false, unitIndex = null) => {
    const imageComponent = {
      name: `image_${Date.now()}`, // Changed: Simplified name
      type: 'image',
      required: false,
      isCommon,
      placeholderimage: '',
      id: `${isCommon ? 'common_' : ''}image_${Date.now()}`
    };

    const updatedStep = { ...step };

    if (isCommon) {
      updatedStep.commonComponents = [...(step.commonComponents || []), imageComponent];
      updatedStep.commonData = {
        ...(step.commonData || {}),
        [imageComponent.name]: null
      };
    } else {
      updatedStep.components = [...(step.components || []), imageComponent];

      // Initialize in all units
      const updatedUnits = (step.units || []).map(unit => {
        const updatedUnit = { ...unit };
        updatedUnit.images = { ...(updatedUnit.images || {}), [imageComponent.name]: null };
        return updatedUnit;
      });

      updatedStep.units = updatedUnits;
    }

    if (onStepUpdate) {
      onStepUpdate(stepIndex, updatedStep);
    }

    toast.success(`Added Image ${isCommon ? 'to common fields' : 'component'}`);
  };

  // Remove component
  const handleRemoveComponent = (componentIndex, isCommon = false) => {
    const components = isCommon ? step.commonComponents : step.components;
    const componentToRemove = components[componentIndex];

    const updatedStep = { ...step };

    if (isCommon) {
      updatedStep.commonComponents = components.filter((_, index) => index !== componentIndex);
      // Remove from common data
      const { [componentToRemove.name]: removed, ...remainingData } = updatedStep.commonData || {};
      updatedStep.commonData = remainingData;
    } else {
      updatedStep.components = components.filter((_, index) => index !== componentIndex);

      // Remove component data from all units
      const updatedUnits = (step.units || []).map(unit => {
        const updatedUnit = { ...unit };

        if (componentToRemove.type === 'image') {
          const { [componentToRemove.name]: removed, ...remainingImages } = updatedUnit.images || {};
          updatedUnit.images = remainingImages;
        } else {
          const { [componentToRemove.name]: removed, ...remainingData } = updatedUnit.data || {};
          updatedUnit.data = remainingData;
        }

        return updatedUnit;
      });

      updatedStep.units = updatedUnits;
    }

    if (onStepUpdate) {
      onStepUpdate(stepIndex, updatedStep);
    }

    toast.success('Component removed');
  };

  const handleComponentUpdate = (componentIndex, property, value, isCommon = false) => {
    const updatedStep = { ...step };

    if (isCommon) {
      // Update the common component definition
      const updatedComponents = [...(step.commonComponents || [])];
      const currentComponent = updatedComponents[componentIndex];
      updatedComponents[componentIndex] = {
        ...currentComponent,
        [property]: value
      };
      updatedStep.commonComponents = updatedComponents;

      // If updating the 'name' property, we need to update data keys as well
      if (property === 'name') {
        const oldName = step.commonComponents[componentIndex].name;
        const newName = value;
        const componentId = currentComponent.id;

        // Find the old data key in commonData
        const oldDataKey = Object.keys(updatedStep.commonData || {}).find(key =>
          key.includes(componentId) || key.includes(oldName)
        );

        if (oldDataKey) {
          // Generate new data key by replacing the old name with new name in the key
          const newDataKey = oldDataKey.replace(oldName, newName);

          // Update commonData
          updatedStep.commonData = { ...updatedStep.commonData };
          updatedStep.commonData[newDataKey] = updatedStep.commonData[oldDataKey];
          delete updatedStep.commonData[oldDataKey];

          // Update data in all units
          updatedStep.units = updatedStep.units.map(unit => {
            if (unit.data && unit.data[oldDataKey] !== undefined) {
              const updatedData = { ...unit.data };
              updatedData[newDataKey] = updatedData[oldDataKey];
              delete updatedData[oldDataKey];
              return { ...unit, data: updatedData };
            }
            return unit;
          });
        }
      }
    } else {
      // Update regular component definition
      const updatedComponents = [...(step.components || [])];
      const currentComponent = updatedComponents[componentIndex];
      updatedComponents[componentIndex] = {
        ...currentComponent,
        [property]: value
      };
      updatedStep.components = updatedComponents;

      // If updating the 'name' property for regular components
      if (property === 'name') {
        const oldName = step.components[componentIndex].name;
        const newName = value;
        const componentId = currentComponent.id;

        // Find the old data key in units
        const sampleUnit = updatedStep.units[0];
        const oldDataKey = sampleUnit && sampleUnit.data ?
          Object.keys(sampleUnit.data).find(key =>
            key.includes(componentId) || key.includes(oldName) || key === oldName
          ) : null;

        if (oldDataKey) {
          // Generate new data key
          let newDataKey;
          if (oldDataKey === oldName) {
            // Direct name match
            newDataKey = newName;
          } else {
            // Replace old name in the key
            newDataKey = oldDataKey.replace(oldName, newName);
          }

          // Update data in all units
          updatedStep.units = updatedStep.units.map(unit => {
            if (unit.data && unit.data[oldDataKey] !== undefined) {
              const updatedData = { ...unit.data };
              updatedData[newDataKey] = updatedData[oldDataKey];
              delete updatedData[oldDataKey];
              return { ...unit, data: updatedData };
            }
            return unit;
          });
        }
      }
    }

    if (onStepUpdate) {
      onStepUpdate(stepIndex, updatedStep);
    }
  };

  // NEW: Handle step description update
  const handleDescriptionUpdate = (newDescription) => {
    const updatedStep = { ...step, description: newDescription };
    if (onStepUpdate) {
      onStepUpdate(stepIndex, updatedStep);
    }
    toast.success('Description updated successfully');
  };

  // NEW: Handle guidance text update
  const handleGuidanceUpdate = (componentIndex, newGuidance, isCommon = false) => {
    const updatedStep = { ...step };

    if (isCommon) {
      const updatedComponents = [...(step.commonComponents || [])];
      updatedComponents[componentIndex] = {
        ...updatedComponents[componentIndex],
        guidance_text: newGuidance
      };
      updatedStep.commonComponents = updatedComponents;
    } else {
      const updatedComponents = [...(step.components || [])];
      updatedComponents[componentIndex] = {
        ...updatedComponents[componentIndex],
        guidance_text: newGuidance
      };
      updatedStep.components = updatedComponents;
    }

    if (onStepUpdate) {
      onStepUpdate(stepIndex, updatedStep);
    }
    toast.success('Guidance updated successfully');
  };

  // NEW: Handle guidance editing start
  const handleGuidanceEdit = (componentIndex, currentGuidance, isCommon = false) => {
    setEditingGuidance({ componentIndex, isCommon });
    setGuidanceValue(currentGuidance || '');
    // Use setTimeout to ensure the text area gets the correct value after render
    setTimeout(() => {
      setGuidanceValue(currentGuidance || '');
    }, 0);
  };

  // NEW: Handle guidance save
  const handleGuidanceSave = () => {
    if (editingGuidance) {
      handleGuidanceUpdate(editingGuidance.componentIndex, guidanceValue, editingGuidance.isCommon);
      setEditingGuidance(null);
      setGuidanceValue('');
    }
  };

  // NEW: Handle guidance cancel
  const handleGuidanceCancel = () => {
    setEditingGuidance(null);
    setGuidanceValue('');
  };

  // Handle component update from DynamicFormComponent (for drag-drop options)
  const handleComponentOptionUpdate = useCallback((componentName, property, value, isCommon) => {
    const updatedStep = { ...step };

    if (isCommon) {
      const componentIndex = (step.commonComponents || []).findIndex(c => c.name === componentName);
      if (componentIndex !== -1) {
        const updatedComponents = [...(step.commonComponents || [])];
        updatedComponents[componentIndex] = {
          ...updatedComponents[componentIndex],
          [property]: value
        };
        updatedStep.commonComponents = updatedComponents;
      }
    } else {
      const componentIndex = (step.components || []).findIndex(c => c.name === componentName);
      if (componentIndex !== -1) {
        const updatedComponents = [...(step.components || [])];
        updatedComponents[componentIndex] = {
          ...updatedComponents[componentIndex],
          [property]: value
        };
        updatedStep.components = updatedComponents;
      }
    }

    if (onStepUpdate) {
      onStepUpdate(stepIndex, updatedStep);
    }
  }, [step, stepIndex, onStepUpdate]);

  // Handle label editing
  const handleLabelEdit = (componentIndex, currentLabel, isCommon = false) => {
    setEditingLabel({ index: componentIndex, isCommon });
    setLabelValue(currentLabel);
  };

  const handleLabelSave = () => {
    if (labelValue.trim() && editingLabel) {
      handleComponentUpdate(editingLabel.index, 'name', labelValue.trim(), editingLabel.isCommon);
      toast.success('Label updated successfully');
    }
    setEditingLabel(null);
    setLabelValue('');
  };

  const handleLabelCancel = () => {
    setEditingLabel(null);
    setLabelValue('');
  };

  // OPTIONS EDITING FUNCTIONS
  const handleEditOptions = (componentIndex, isCommon = false) => {
    const components = isCommon ? step.commonComponents : step.components;
    const component = components[componentIndex];

    if (!['checkbox', 'radio', 'dropdown', 'defined'].includes(component.type)) {
      return;
    }

    setEditingOptions({ componentIndex, isCommon });
    setTempOptions([...(component.options || [])]);
    setOptionsModalVisible(true);
  };

  const handleAddOption = () => {
    setTempOptions([...tempOptions, '']);
  };

  const handleRemoveOption = (optionIndex) => {
    if (tempOptions.length > 1) {
      setTempOptions(tempOptions.filter((_, index) => index !== optionIndex));
    } else {
      toast.error('At least one option is required');
    }
  };

  const handleOptionChange = (optionIndex, value) => {
    const updatedOptions = [...tempOptions];
    updatedOptions[optionIndex] = value;
    setTempOptions(updatedOptions);
  };

  const handleSaveOptions = () => {
    const validOptions = tempOptions.filter(option => option.trim() !== '');

    if (validOptions.length === 0) {
      toast.error('At least one valid option is required');
      return;
    }

    handleComponentUpdate(
      editingOptions.componentIndex,
      'options',
      validOptions,
      editingOptions.isCommon
    );

    setOptionsModalVisible(false);
    setEditingOptions(null);
    setTempOptions([]);
    toast.success('Options updated successfully');
  };

  const handleCancelOptions = () => {
    setOptionsModalVisible(false);
    setEditingOptions(null);
    setTempOptions([]);
  };

  // Handle text drag from categories
  const handleTextDragStart = (e, text) => {
    e.dataTransfer.setData('text/plain', text);
    e.dataTransfer.effectAllowed = 'copy';
    e.target.style.opacity = '0.7';
  };

  const handleTextDragEnd = (e) => {
    e.target.style.opacity = '1';
  };

  // Check if component has options
  const componentHasOptions = (component) => {
    return ['checkbox', 'radio', 'dropdown', 'defined'].includes(component.type);
  };

   return (
    <div className="relative">
      <div >
        {/* NEW: Editable Description */}
        {editingDescription ? (
          <div className="mb-4">
            <Paragraph 
              editable={{
                onChange: (value) => handleDescriptionUpdate(value),
                onEnd: () => setEditingDescription(false),
                text: step?.description || ''
              }}
              className="mb-0 font-semibold text-gray-700"
            >
              {step?.description || 'Click to add description...'}
            </Paragraph>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-4">
            <Typography.Text className="mb-0 font-semibold text-gray-700">
              {step?.description || 'No description set'}
            </Typography.Text>
            {isEditMode && (
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={() => setEditingDescription(true)}
                className="opacity-60 hover:opacity-100"
                title="Edit Description"
              />
            )}
          </div>
        )}

        {step?.guidance?.length > 0 ?(
          <div>
            <ul className="my-2 list-disc list-inside space-y-1">
              {step.guidance.map((item, idx) => (
                <li key={idx} className="text-gray-700">
                  <Typography.Text>{item}</Typography.Text>
                </li>
              ))}
            </ul>
          </div>
        ):""}

        {/* Show message for new steps that haven't been configured */}
        {isNewStep && (!step.components || step.components.length === 0) && !isEditMode && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <Typography.Text type="secondary">
              This is a new step. Click "Edit Step" to configure form elements.
            </Typography.Text>
          </div>
        )}
         {/* Unit-specific components */}
        {(step.components && step.components.length > 0 || isEditMode) ? (
          <div className="mb-6">
            <Row gutter={[16, 16]}>
              {step.units?.map((unit, unitIndex) => (
                <Col xs={24} sm={12} lg={8} xl={6} key={unitIndex}>
                  <Card
                    title={
                      <Controller
                        name={`steps.${stepIndex}.units.${unitIndex}.name`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            disabled={true}
                            {...field}
                            size="small"
                            style={{ width: "50%" }}
                            onChange={(e) => {
                              field.onChange(e);
                              onFormChange(
                                `steps.${stepIndex}.units.${unitIndex}.name`,
                                e.target.value,
                              );
                            }}
                          />
                        )}
                      />
                    }
                    size="small"
                    className={`${isEditMode ? 'edit-mode-unit' : ''} ${dragOverZone === `unit-${unitIndex}` ? 'drag-over-highlight' : ''
                      }`}
                  >
                    <div
                      style={{
                        overflowY: "auto",
                        minHeight: isEditMode ? "200px" : "auto",
                        position: 'relative'
                      }}
                      onDrop={isEditMode ? (e) => handleUnitComponentDrop(e, unitIndex) : undefined}
                      onDragOver={isEditMode ? (e) => handleUnitDragOver(e, unitIndex) : undefined}
                      onDragLeave={isEditMode ? handleDragLeave : undefined}
                      className={isEditMode ? 'drop-zone' : ''}
                    >
                      {step.components?.length ? step.components?.map((component, componentIndex) => (
                        <div key={`${component.name}-${componentIndex}`} className="relative mb-4">
                          {/* Show editable label only for first unit (index 0) in edit mode OR regular label for other units */}
                          {isEditMode && unitIndex === 0 ? (
                            <EditableLabel
                              name={component.name}
                              componentIndex={componentIndex}
                              isCommon={false}
                              component={component}
                              unitIndex={unitIndex}
                              editingLabel={editingLabel}
                              labelValue={labelValue}
                              setLabelValue={setLabelValue}
                              handleLabelEdit={handleLabelEdit}
                              handleLabelSave={handleLabelSave}
                              handleLabelCancel={handleLabelCancel}
                              handleEditOptions={handleEditOptions}
                              handleRemoveComponent={handleRemoveComponent}
                              componentHasOptions={componentHasOptions}
                              isEditMode={isEditMode}
                              editingGuidance={editingGuidance}
                              guidanceValue={guidanceValue}
                              setGuidanceValue={setGuidanceValue}
                              handleGuidanceEdit={handleGuidanceEdit}
                              handleGuidanceSave={handleGuidanceSave}
                              handleGuidanceCancel={handleGuidanceCancel}
                            />
                          ) : (
                            <div className='flex justify-between'>
                              <Typography.Text strong style={{ display: "block", marginBottom: "8px" }}>
                                {component.name}
                                {component?.unit && ` (${component.unit})`}
                                {component?.required && <span style={{ color: "red" }}>*</span>}
                              </Typography.Text>

                              {component.guidance_text ? <Tooltip title={component.guidance_text}>
                                <QuestionCircleOutlined className="me-3" style={{ color: "gray", fontSize: "14px", cursor: "pointer" }} />
                              </Tooltip> : ""}
                            </div>
                          )}
                          <DynamicFormComponent
                            component={component}
                            control={control}
                            stepIndex={stepIndex}
                            unitIndex={unitIndex}
                            currentValue={
                              component.type === "image"
                                ? unit.images?.[component.name]
                                : unit.data?.[component.name]
                            }
                            onDataChange={handleUnitDataChange}
                            onImageChange={handleImageUpload}
                            onRemoveImage={handleImageRemove}
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            onDrop={onDrop}
                            dragOverTarget={dragOverTarget}
                            isCommon={false}
                            stepname={step.name}
                            hideLabel={true}
                            onComponentUpdate={handleComponentOptionUpdate}
                          />
                        </div>
                      )) :
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }} className='text-center my-20'>
                          <Typography.Title level={5} className='w-100 font-12'>
                            Create using (Add Component OR Add Image)
                          </Typography.Title>

                        </div>}

                      {/* Drop zone indicator for all units in edit mode */}

                    </div>

                    {/* Add buttons for all units in edit mode */}
                    {isEditMode && unitIndex == 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                        <InspectionBtn
                          variant="primary"
                          size="xs"
                          onClick={() => handleAddImageComponent(false, unitIndex)}
                          icon={<CameraOutlined />}
                        >
                          Add Image
                        </InspectionBtn>

                        <Select
                          placeholder="Add Component"
                          size="small"
                          className="w-full"
                          onChange={(value) => handleAddComponent(value, false)}
                          value={null}
                        >
                          {formComponents.map(component => (
                            <Select.Option key={component.type} value={component.type}>
                              <component.icon className="mr-2" />
                              {component.label}
                            </Select.Option>
                          ))}
                        </Select>

                      </div>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ) : null}

        {/* Common components section */}
        <div className="mt-6">
          <Typography.Text strong className="block mb-3">{step.components ? "Remarks":"Data Points"}</Typography.Text>
          <Card
            style={{
              backgroundColor: dragOverZone === 'common' ? "#e6f7ff" : "#F3F4F6",
              borderColor: dragOverZone === 'common' ? "#1890ff" : "lightgrey",
              transition: "all 0.3s ease"
            }}
            className={dragOverZone === 'common' ? 'drag-over-highlight' : ''}
          >
            <div
              style={{ minHeight: isEditMode ? "100px" : "auto" }}
              onDrop={isEditMode ? handleCommonComponentDrop : undefined}
              onDragOver={isEditMode ? handleCommonDragOver : undefined}
              onDragLeave={isEditMode ? handleDragLeave : undefined}
            >
              <Row gutter={[24, 16]}>
                {step.commonComponents?.map((component, componentIndex) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={`${component.name}-${componentIndex}`}>
                    <div className="relative">
                      <EditableLabel
                        name={component.name}
                        componentIndex={componentIndex}
                        isCommon={true}
                        component={component}
                        unitIndex={null}
                        editingLabel={editingLabel}
                        labelValue={labelValue}
                        setLabelValue={setLabelValue}
                        handleLabelEdit={handleLabelEdit}
                        handleLabelSave={handleLabelSave}
                        handleLabelCancel={handleLabelCancel}
                        handleEditOptions={handleEditOptions}
                        handleRemoveComponent={handleRemoveComponent}
                        componentHasOptions={componentHasOptions}
                        isEditMode={isEditMode}
                        editingGuidance={editingGuidance}
                        guidanceValue={guidanceValue}
                        setGuidanceValue={setGuidanceValue}
                        handleGuidanceEdit={handleGuidanceEdit}
                        handleGuidanceSave={handleGuidanceSave}
                        handleGuidanceCancel={handleGuidanceCancel}
                      />

                      <DynamicFormComponent
                        component={component}
                        control={control}
                        stepIndex={stepIndex}
                        unitIndex={null}
                        currentValue={
                          component.type === "image"
                            ? step.commonData?.[component.name]
                            : step.commonData?.[component.name]
                        }
                        onDataChange={handleCommonDataChange}
                        onImageChange={handleImageUpload}
                        onRemoveImage={handleImageRemove}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                        dragOverTarget={dragOverTarget}
                        isCommon={true}
                        hideLabel={true}
                        onComponentUpdate={handleComponentOptionUpdate}
                      />
                    </div>
                  </Col>
                ))}

                {/* Add buttons for common components in edit mode */}
                {isEditMode && (
                  <Col xs={24}>
                    <div className="mt-3 space-y-2">
                      <InspectionBtn
                        variant="primary"
                        size="xs"
                        onClick={() => handleAddImageComponent(true)}
                        icon={<CameraOutlined />}
                      >
                        Add Image
                      </InspectionBtn>


                      {/* Add Common Component Dropdown */}
                      <Select
                        placeholder="Add Common Component"
                        size="small"
                        className="w-full"
                        onChange={(value) => handleAddComponent(value, true)}
                        value={null}
                      >
                        {formComponents.map(component => (
                          <Select.Option key={component.type} value={component.type}>
                            <component.icon className="mr-2" />
                            {component.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          </Card>
        </div>

        {/* Remarks section if configured */}
        {step.remarkField && (
          <div className="mt-3">
            <Card title="Remarks">
              <Input.TextArea
                placeholder="Enter remarks..."
                rows={3}
                value={step.commonData?.remarks || ''}
                onChange={(e) => handleCommonDataChange(stepIndex, 'remarks', e.target.value)}
              />
            </Card>
          </div>
        )}
      </div>

      {/* Options Editing Modal */}
      <Modal
        title={
          <div className="flex items-center">
            <SettingOutlined className="mr-2" />
            Edit Options
          </div>
        }
        open={optionsModalVisible}
        onOk={handleSaveOptions}
        onCancel={handleCancelOptions}
        width={500}
        okText="Save Options"
        cancelText="Cancel"
      >


        <div className="space-y-2 max-h-60 overflow-y-auto">
          {tempOptions.map((option, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                placeholder={`Option ${index + 1}`}
                className="flex-1"
              />
              <Button
                type="text"
                danger
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemoveOption(index)}
                disabled={tempOptions.length === 1}
              />
            </div>
          ))}
        </div>

        <div className="mt-4">
          <Button
            type="dashed"
            icon={<PlusCircleOutlined />}
            onClick={handleAddOption}
            className="w-full"
          >
            Add Option
          </Button>
        </div>

        <div className="mt-4 p-3 bg-green-50 rounded">
          <Typography.Text type="secondary" className="text-sm">
            <strong>Tip:</strong> You can drag and drop text from the "Text Library" in the toolbox
            directly
          </Typography.Text>
        </div>
      </Modal>

      <FloatingToolbox
        isEditMode={true}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        handleTextDragStart={handleTextDragStart}
        handleTextDragEnd={handleTextDragEnd}
      />

      {/* Custom Styles */}
      <style jsx>{`
        .edit-mode-unit {
           box-shadow: 0 0 10px rgba(24, 144, 255, 0.2);
        }
        
        .drag-over-highlight {
          border: 2px solid #1890ff !important;
          box-shadow: 0 0 15px rgba(24, 144, 255, 0.3);
          transform: scale(1.02);
          transition: all 0.3s ease;
        }
        
        .drop-zone {
          position: relative;
          border: 2px dashed transparent;
          transition: all 0.3s ease;
        }
        
        .drop-zone.drag-over {
          border-color: #1890ff;
          background-color: #f0f8ff;
        }
        
        .drop-zone-indicator {
          margin: 10px 0;
        }
        
        .touch-manipulation {
          touch-action: manipulation;
        }
        
        /* Ensure proper drag and drop zones */
        .drop-zone::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          border: 2px dashed transparent;
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        
        .drop-zone:not(:empty)::before {
          display: none;
        }
        
        @media (max-width: 768px) {
          .mobile-toolbox .ant-drawer-content {
            padding: 16px;
          }
          
          .ant-col {
            margin-bottom: 16px;
          }
          
          .ant-card {
            margin-bottom: 16px;
          }
          
          .drop-zone-indicator {
            padding: 20px;
          }
        }
        
        @media (hover: hover) {
          .hover\\:shadow-lg:hover {
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          }
        }
        
        /* Fix for drag over states */
        .drop-zone[data-drag-over="true"] {
          background-color: #f0f8ff !important;
          border-color: #1890ff !important;
        }
        
        /* Improve visual feedback */
        .component-item {
          position: relative;
          transition: all 0.2s ease;
        }
        
        .component-item:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .component-overlay {
          position: absolute;
          top: -5px;
          right: -5px;
          z-index: 10;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        
        .component-item:hover .component-overlay {
          opacity: 1;
        }
        
        /* Button spacing */
        .space-y-2 > * + * {
          margin-top: 8px;
        }
        
        /* Enhanced button styles */
        .ant-btn-dashed {
          transition: all 0.2s ease;
        }
        
        .ant-btn-dashed:hover {
          transform: translateY(-1px);
        }

        /* Options modal styling */
        .ant-modal-body .space-y-2 > * + * {
          margin-top: 8px;
        }

        /* NEW: Guidance text styling */
        .guidance-text {
          background: #f8f9fa;
          border-left: 3px solid #1890ff;
          padding: 8px 12px;
          margin: 8px 0;
          border-radius: 4px;
          font-size: 12px;
        }
        
        .guidance-edit-area {
          border: 1px solid #d9d9d9;
          border-radius: 6px;
          padding: 8px;
          background: #fafafa;
        }
      `}</style>
    </div>
  );
};

export default InspectionStepContent;